import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { 
  Plus, 
  Edit3, 
  Trash2, 
  Image as ImageIcon,
  Check,
  Clock,
  Circle,
  ChevronDown,
  X,
  Sparkles,
  Upload,
  Filter,
  Download,
  Layers,
  Grid3X3
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

// Get auth headers
const getAuthHeaders = () => {
  const token = sessionStorage.getItem("prosperMindToken") || sessionStorage.getItem("authToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Status configuration
const STATUS_CONFIG = {
  NOT_STARTED: {
    label: "Not Started",
    color: "#6B7280",
    bgColor: "#F3F4F6",
    borderColor: "#E5E7EB"
  },
  IN_PROGRESS: {
    label: "In Progress",
    color: "#3B82F6",
    bgColor: "#EFF6FF",
    borderColor: "#BFDBFE"
  },
  ACHIEVED: {
    label: "Achieved",
    color: "#10B981",
    bgColor: "#D1FAE5",
    borderColor: "#6EE7B7"
  }
};

// Default categories
const CATEGORIES = [
  { value: "Lifestyle", label: "Lifestyle" },
  { value: "Work / Business", label: "Work / Business" },
  { value: "Time Freedom", label: "Time Freedom" },
  { value: "Relationships", label: "Relationships" },
  { value: "Health", label: "Health" },
  { value: "Impact / Legacy", label: "Impact / Legacy" },
  { value: "Custom", label: "Custom" }
];

// Status Chip Component
const StatusChip = ({ status, onClick, showDropdown = false }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.NOT_STARTED;
  const [isOpen, setIsOpen] = useState(false);

  const handleStatusChange = (newStatus) => {
    onClick(newStatus);
    setIsOpen(false);
  };

  if (!showDropdown) {
    return (
      <span
        className="px-3 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1.5"
        style={{ 
          backgroundColor: config.bgColor, 
          color: config.color,
          border: `1px solid ${config.borderColor}`
        }}
      >
        {status === "ACHIEVED" && <Check className="w-3 h-3" />}
        {status === "IN_PROGRESS" && <Clock className="w-3 h-3" />}
        {status === "NOT_STARTED" && <Circle className="w-3 h-3" />}
        {config.label}
      </span>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={(e) => { e.stopPropagation(); setIsOpen(!isOpen); }}
        className="px-3 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1.5 transition-all hover:opacity-80"
        style={{ 
          backgroundColor: config.bgColor, 
          color: config.color,
          border: `1px solid ${config.borderColor}`
        }}
      >
        {status === "ACHIEVED" && <Check className="w-3 h-3" />}
        {status === "IN_PROGRESS" && <Clock className="w-3 h-3" />}
        {status === "NOT_STARTED" && <Circle className="w-3 h-3" />}
        {config.label}
        <ChevronDown className="w-3 h-3" />
      </button>
      
      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={(e) => { e.stopPropagation(); setIsOpen(false); }} 
          />
          <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50 min-w-[140px]">
            {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
              <button
                key={key}
                onClick={(e) => { e.stopPropagation(); handleStatusChange(key); }}
                className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 transition-colors"
                style={{ color: cfg.color }}
              >
                {key === "ACHIEVED" && <Check className="w-4 h-4" />}
                {key === "IN_PROGRESS" && <Clock className="w-4 h-4" />}
                {key === "NOT_STARTED" && <Circle className="w-4 h-4" />}
                {cfg.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Vision Card Component
const VisionCard = ({ item, onEdit, onDelete, onStatusChange }) => {
  const [isAchievedAnimation, setIsAchievedAnimation] = useState(false);
  const [imageError, setImageError] = useState(false);
  const isAchieved = item.status === "ACHIEVED";
  
  // Check if image is a base64 data URL (persistent) or old file path (may be broken)
  const isBase64Image = item.image_url && item.image_url.startsWith('data:');
  const hasOldFilePath = item.image_url && !item.image_url.startsWith('data:');

  const handleStatusChange = async (newStatus) => {
    if (newStatus === "ACHIEVED" && item.status !== "ACHIEVED") {
      setIsAchievedAnimation(true);
      setTimeout(() => setIsAchievedAnimation(false), 2000);
    }
    await onStatusChange(item.id, newStatus);
  };

  // Get the proper image source
  const getImageSrc = () => {
    if (!item.image_url) return null;
    if (isBase64Image) return item.image_url;
    if (item.image_url.startsWith('/api/uploads/')) {
      return `${API}${item.image_url.replace('/api/uploads/', '/uploads/')}`;
    }
    return `${API}${item.image_url}`;
  };

  const showFallback = !item.image_url || imageError;

  return (
    <div 
      className={`group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-500 border ${
        isAchieved 
          ? 'border-emerald-200 ring-2 ring-emerald-100' 
          : 'border-gray-100 hover:border-gray-200'
      } ${isAchievedAnimation ? 'animate-pulse' : ''}`}
    >
      {/* Image Section */}
      <div className="relative aspect-[16/10] bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden">
        {item.image_url && !imageError ? (
          <img 
            src={getImageSrc()}
            alt={item.title}
            className="w-full h-full object-cover"
            onError={() => setImageError(true)}
          />
        ) : null}
        {/* Fallback for broken images or no image */}
        {showFallback && (
          <div className="w-full h-full flex items-center justify-center absolute inset-0 bg-gradient-to-br from-gray-50 to-gray-100">
            <div className="text-center p-6">
              <ImageIcon className="w-12 h-12 text-gray-300 mx-auto mb-2" />
              {hasOldFilePath && imageError ? (
                <>
                  <p className="text-xs text-amber-600 font-medium">Image needs re-upload</p>
                  <button 
                    onClick={(e) => { e.stopPropagation(); onEdit(item); }}
                    className="mt-2 px-3 py-1.5 bg-amber-100 text-amber-700 rounded-lg text-xs font-medium hover:bg-amber-200 transition-colors"
                  >
                    Re-upload Image
                  </button>
                </>
              ) : (
                <p className="text-xs text-gray-400">No image yet</p>
              )}
            </div>
          </div>
        )}
        
        {/* Category Badge */}
        <div className="absolute top-3 left-3">
          <span className="px-2.5 py-1 bg-white/90 backdrop-blur-sm rounded-full text-xs font-medium text-gray-700 shadow-sm">
            {item.category}
          </span>
        </div>

        {/* Edit/Delete Actions */}
        <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(item); }}
            className="p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-sm hover:bg-white transition-colors"
          >
            <Edit3 className="w-4 h-4 text-gray-600" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(item.id); }}
            className="p-2 bg-white/90 backdrop-blur-sm rounded-full shadow-sm hover:bg-red-50 transition-colors"
          >
            <Trash2 className="w-4 h-4 text-gray-600 hover:text-red-500" />
          </button>
        </div>

        {/* Achieved Glow Effect */}
        {isAchieved && (
          <div className="absolute inset-0 bg-gradient-to-t from-emerald-500/10 to-transparent pointer-events-none" />
        )}
      </div>

      {/* Content Section */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-3 mb-3">
          <h3 className="font-semibold text-gray-900 text-lg leading-tight line-clamp-2">
            {item.title}
          </h3>
          <StatusChip 
            status={item.status} 
            onClick={handleStatusChange}
            showDropdown={true}
          />
        </div>

        {item.description_what && (
          <p className="text-sm text-gray-500 line-clamp-2 leading-relaxed">
            {item.description_what}
          </p>
        )}

        {/* Achieved Badge */}
        {isAchieved && item.achieved_at && (
          <div className="mt-3 pt-3 border-t border-emerald-100">
            <p className="text-xs text-emerald-600 font-medium flex items-center gap-1.5">
              <Sparkles className="w-3.5 h-3.5" />
              Achieved {new Date(item.achieved_at).toLocaleDateString()}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// Add/Edit Modal Component
const VisionModal = ({ isOpen, onClose, onSave, editingItem }) => {
  const [formData, setFormData] = useState({
    title: "",
    category: "Lifestyle",
    customCategory: "",
    description_what: "",
    description_why: "",
    status: "NOT_STARTED"
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [imagePreviewError, setImagePreviewError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (editingItem) {
      const isCustom = !CATEGORIES.slice(0, -1).find(c => c.value === editingItem.category);
      setFormData({
        title: editingItem.title || "",
        category: isCustom ? "Custom" : editingItem.category,
        customCategory: isCustom ? editingItem.category : "",
        description_what: editingItem.description_what || "",
        description_why: editingItem.description_why || "",
        status: editingItem.status || "NOT_STARTED"
      });
      setImagePreviewError(false);
      if (editingItem.image_url) {
        // Handle both base64 data URLs, /api/uploads paths, and direct uploads paths
        let imageUrl = editingItem.image_url;
        if (!imageUrl.startsWith('data:')) {
          if (imageUrl.startsWith('/api/uploads/')) {
            imageUrl = `${API}${imageUrl.replace('/api/uploads/', '/uploads/')}`;
          } else {
            imageUrl = `${API}${imageUrl}`;
          }
        }
        setImagePreview(imageUrl);
      } else {
        setImagePreview(null);
      }
    } else {
      setFormData({
        title: "",
        category: "Lifestyle",
        customCategory: "",
        description_what: "",
        description_why: "",
        status: "NOT_STARTED"
      });
      setImagePreview(null);
    }
    setImageFile(null);
  }, [editingItem, isOpen]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error("Please select a valid image file");
        return;
      }
      // Validate file size (2MB max for base64 storage)
      if (file.size > 2 * 1024 * 1024) {
        toast.error("Image must be less than 2MB");
        return;
      }
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error("Please enter a title for your vision");
      return;
    }
    
    setIsLoading(true);
    try {
      const itemData = {
        title: formData.title,
        category: formData.category,
        custom_category: formData.category === "Custom" ? formData.customCategory : null,
        description_what: formData.description_what,
        description_why: formData.description_why,
        status: formData.status
      };
      
      await onSave(itemData, imageFile, editingItem?.id);
      onClose();
    } catch (error) {
      console.error("Error saving vision item:", error);
      toast.error("Failed to save vision item");
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              {editingItem ? "Edit Vision Item" : "Add Vision Item"}
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="p-6 space-y-6">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder="Example: Beach house in Baja"
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-gray-400 focus:ring-0 transition-colors text-gray-900 placeholder-gray-400"
              />
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Category
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-gray-400 focus:ring-0 transition-colors text-gray-900 bg-white"
              >
                {CATEGORIES.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
              
              {formData.category === "Custom" && (
                <input
                  type="text"
                  value={formData.customCategory}
                  onChange={(e) => setFormData({...formData, customCategory: e.target.value})}
                  placeholder="Enter custom category"
                  className="w-full mt-3 px-4 py-3 rounded-xl border border-gray-200 focus:border-gray-400 focus:ring-0 transition-colors text-gray-900 placeholder-gray-400"
                />
              )}
            </div>

            {/* Description What */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Describe it in detail
              </label>
              <p className="text-xs text-gray-400 mb-2">
                Write it like a scene. Where are you? What does a normal day look like? Who's with you?
              </p>
              <textarea
                value={formData.description_what}
                onChange={(e) => setFormData({...formData, description_what: e.target.value})}
                rows={4}
                placeholder="Make it specific. The details matter."
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-gray-400 focus:ring-0 transition-colors text-gray-900 placeholder-gray-400 resize-none"
              />
            </div>

            {/* Description Why */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Why does this matter to you?
              </label>
              <p className="text-xs text-gray-400 mb-2">
                What feeling, value, or change are you really chasing?
              </p>
              <textarea
                value={formData.description_why}
                onChange={(e) => setFormData({...formData, description_why: e.target.value})}
                rows={3}
                placeholder="Clarity creates momentum."
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-gray-400 focus:ring-0 transition-colors text-gray-900 placeholder-gray-400 resize-none"
              />
            </div>

            {/* Image Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Add Image
              </label>
              <p className="text-xs text-gray-400 mb-3">
                Images make your vision real.
              </p>
              
              <div 
                onClick={() => fileInputRef.current?.click()}
                className={`relative border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all
                  ${imagePreview && !imagePreviewError
                    ? 'border-gray-200 bg-gray-50' 
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
              >
                {imagePreview && !imagePreviewError ? (
                  <div className="relative">
                    <img 
                      src={imagePreview} 
                      alt="Preview" 
                      className="max-h-48 mx-auto rounded-lg"
                      onError={() => setImagePreviewError(true)}
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setImagePreview(null);
                        setImageFile(null);
                        setImagePreviewError(false);
                      }}
                      className="absolute -top-2 -right-2 p-1.5 bg-white rounded-full shadow-md hover:bg-gray-100"
                    >
                      <X className="w-4 h-4 text-gray-500" />
                    </button>
                  </div>
                ) : imagePreviewError ? (
                  <div className="py-4">
                    <ImageIcon className="w-10 h-10 text-amber-400 mx-auto mb-3" />
                    <p className="text-sm text-amber-600 font-medium">Previous image unavailable</p>
                    <p className="text-xs text-gray-500 mt-1">Click to upload a new image</p>
                  </div>
                ) : (
                  <div className="py-4">
                    <Upload className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm text-gray-500">Click to upload an image</p>
                    <p className="text-xs text-gray-400 mt-1">PNG, JPG up to 10MB</p>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </div>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <div className="flex gap-2 flex-wrap">
                {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setFormData({...formData, status: key})}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                      formData.status === key
                        ? 'ring-2 ring-offset-2'
                        : 'opacity-60 hover:opacity-100'
                    }`}
                    style={{ 
                      backgroundColor: config.bgColor, 
                      color: config.color,
                      ringColor: config.color
                    }}
                  >
                    {config.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-200 rounded-xl transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-6 py-2.5 text-sm font-medium text-white bg-gray-900 hover:bg-gray-800 rounded-xl transition-colors disabled:opacity-50"
            >
              {isLoading ? "Saving..." : (editingItem ? "Save Changes" : "Create Vision")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Empty State Component
const EmptyState = ({ onAdd }) => (
  <div className="text-center py-20">
    <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
      <Sparkles className="w-10 h-10 text-gray-400" />
    </div>
    <h3 className="text-xl font-semibold text-gray-900 mb-2">Start with one thing you truly want.</h3>
    <p className="text-gray-500 mb-8 max-w-md mx-auto">
      Your vision is personal. Take a moment to think about what matters most to you.
    </p>
    <button
      onClick={onAdd}
      className="inline-flex items-center gap-2 px-6 py-3 bg-gray-900 text-white rounded-xl font-medium hover:bg-gray-800 transition-colors"
    >
      <Plus className="w-5 h-5" />
      Add Vision Item
    </button>
  </div>
);

// Page size configurations for collage
const PAGE_SIZES = {
  letter: { name: "Letter", width: 2550, height: 3300, aspect: "8.5 × 11 in" },
  legal: { name: "Legal", width: 2550, height: 4200, aspect: "8.5 × 14 in" },
  square: { name: "Square", width: 3000, height: 3000, aspect: "1:1" },
  poster: { name: "Poster", width: 2400, height: 3600, aspect: "24 × 36 in" }
};

const MIN_IMAGES_FOR_COLLAGE = 10;

// Collage Progress Section Component
const CollageProgressSection = ({ items, onOpenCollage }) => {
  const itemsWithImages = items.filter(item => item.image_url && item.image_url.startsWith('data:'));
  const imageCount = itemsWithImages.length;
  const progress = Math.min((imageCount / MIN_IMAGES_FOR_COLLAGE) * 100, 100);
  const canCreateCollage = imageCount >= MIN_IMAGES_FOR_COLLAGE;

  return (
    <div className="bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-200 rounded-2xl p-6" data-testid="collage-progress-section">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
              <Layers className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900" data-testid="collage-title">Vision Board Collage</h3>
              <p className="text-sm text-gray-500">Create a downloadable collage of your dreams</p>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700" data-testid="collage-image-count">
                {imageCount} / {MIN_IMAGES_FOR_COLLAGE} images
              </span>
              {canCreateCollage ? (
                <span className="text-xs text-emerald-600 font-medium flex items-center gap-1" data-testid="collage-ready-text">
                  <Check className="w-3 h-3" /> Ready to create!
                </span>
              ) : (
                <span className="text-xs text-purple-600" data-testid="collage-needed-text">
                  {MIN_IMAGES_FOR_COLLAGE - imageCount} more needed
                </span>
              )}
            </div>
            <div className="w-full bg-purple-100 rounded-full h-3 overflow-hidden" data-testid="collage-progress-bar">
              <div 
                className={`h-full rounded-full transition-all duration-500 ${
                  canCreateCollage 
                    ? 'bg-gradient-to-r from-emerald-500 to-teal-500' 
                    : 'bg-gradient-to-r from-purple-500 to-indigo-500'
                }`}
                style={{ width: `${progress}%` }}
                data-testid="collage-progress-fill"
              />
            </div>
          </div>
        </div>
        
        <button
          onClick={onOpenCollage}
          disabled={!canCreateCollage}
          data-testid="create-collage-btn"
          className={`px-5 py-2.5 rounded-xl font-medium transition-all flex items-center gap-2 ${
            canCreateCollage
              ? 'bg-purple-600 text-white hover:bg-purple-700 shadow-lg shadow-purple-200'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          <Grid3X3 className="w-4 h-4" />
          Create Collage
        </button>
      </div>
    </div>
  );
};

// Collage Generator Modal Component
const CollageGeneratorModal = ({ isOpen, onClose, items }) => {
  const [selectedSize, setSelectedSize] = useState('letter');
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [heroImages, setHeroImages] = useState([]); // IDs of hero images (max 2)
  const [step, setStep] = useState(1); // 1 = select heroes, 2 = preview & download
  const canvasRef = useRef(null);
  
  const itemsWithImages = items.filter(item => item.image_url && item.image_url.startsWith('data:'));

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep(1);
      setHeroImages([]);
      setPreviewUrl(null);
    }
  }, [isOpen]);

  // Toggle hero image selection (max 2)
  const toggleHeroImage = (itemId) => {
    setHeroImages(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      }
      if (prev.length >= 2) {
        // Replace oldest selection
        return [...prev.slice(1), itemId];
      }
      return [...prev, itemId];
    });
  };

  // Load image from URL
  const loadImage = (src) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  };

  // Generate Mosaic/Magazine layout positions
  const generateMosaicPositions = useCallback((imageCount, heroIndices, canvasWidth, canvasHeight) => {
    const positions = [];
    const gap = 8; // Small gap between images
    const headerHeight = 100;
    const footerHeight = 60;
    const padding = 30;
    
    const availableWidth = canvasWidth - (padding * 2);
    const availableHeight = canvasHeight - headerHeight - footerHeight - (padding * 2);
    const startY = headerHeight + padding;
    const startX = padding;
    
    // Separate hero and regular images
    const heroCount = heroIndices.length;
    const regularCount = imageCount - heroCount;
    
    if (heroCount === 0) {
      // No heroes - use a clean grid layout
      const cols = 4;
      const rows = Math.ceil(imageCount / cols);
      const cellWidth = (availableWidth - (gap * (cols - 1))) / cols;
      const cellHeight = (availableHeight - (gap * (rows - 1))) / rows;
      
      for (let i = 0; i < imageCount; i++) {
        const col = i % cols;
        const row = Math.floor(i / cols);
        positions.push({
          x: startX + col * (cellWidth + gap),
          y: startY + row * (cellHeight + gap),
          width: cellWidth,
          height: cellHeight,
          isHero: false
        });
      }
    } else if (heroCount === 1) {
      // 1 hero on left (takes 50% width, full height), regular images on right in grid
      const heroWidth = availableWidth * 0.5 - gap / 2;
      const heroHeight = availableHeight;
      
      // Hero position (index 0 in positions will be the hero)
      positions.push({
        x: startX,
        y: startY,
        width: heroWidth,
        height: heroHeight,
        isHero: true,
        heroIndex: 0
      });
      
      // Regular images on the right
      const rightStartX = startX + heroWidth + gap;
      const rightWidth = availableWidth - heroWidth - gap;
      const cols = 2;
      const rows = Math.ceil(regularCount / cols);
      const cellWidth = (rightWidth - (gap * (cols - 1))) / cols;
      const cellHeight = (availableHeight - (gap * (rows - 1))) / rows;
      
      for (let i = 0; i < regularCount; i++) {
        const col = i % cols;
        const row = Math.floor(i / cols);
        positions.push({
          x: rightStartX + col * (cellWidth + gap),
          y: startY + row * (cellHeight + gap),
          width: cellWidth,
          height: cellHeight,
          isHero: false
        });
      }
    } else {
      // 2 heroes - one large on top-left, one medium on top-right, rest below
      const topHeight = availableHeight * 0.55;
      const bottomHeight = availableHeight - topHeight - gap;
      
      // First hero - large, top-left (60% width)
      const hero1Width = availableWidth * 0.6 - gap / 2;
      positions.push({
        x: startX,
        y: startY,
        width: hero1Width,
        height: topHeight,
        isHero: true,
        heroIndex: 0
      });
      
      // Second hero - medium, top-right (40% width)
      const hero2Width = availableWidth - hero1Width - gap;
      positions.push({
        x: startX + hero1Width + gap,
        y: startY,
        width: hero2Width,
        height: topHeight,
        isHero: true,
        heroIndex: 1
      });
      
      // Regular images at the bottom in a row
      const bottomStartY = startY + topHeight + gap;
      const cols = Math.min(regularCount, 5);
      const cellWidth = (availableWidth - (gap * (cols - 1))) / cols;
      const rows = Math.ceil(regularCount / cols);
      const cellHeight = (bottomHeight - (gap * (rows - 1))) / rows;
      
      for (let i = 0; i < regularCount; i++) {
        const col = i % cols;
        const row = Math.floor(i / cols);
        positions.push({
          x: startX + col * (cellWidth + gap),
          y: bottomStartY + row * (cellHeight + gap),
          width: cellWidth,
          height: cellHeight,
          isHero: false
        });
      }
    }
    
    return positions;
  }, []);

  // Generate the collage
  const generateCollage = useCallback(async () => {
    if (itemsWithImages.length < MIN_IMAGES_FOR_COLLAGE) return;
    
    setIsGenerating(true);
    
    try {
      const size = PAGE_SIZES[selectedSize];
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      
      canvas.width = size.width;
      canvas.height = size.height;
      
      // Background - elegant dark gradient
      const gradient = ctx.createLinearGradient(0, 0, size.width, size.height);
      gradient.addColorStop(0, '#0f0f1a');
      gradient.addColorStop(0.5, '#1a1a2e');
      gradient.addColorStop(1, '#0d0d14');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, size.width, size.height);
      
      // Subtle grain texture
      ctx.fillStyle = 'rgba(255, 255, 255, 0.015)';
      for (let i = 0; i < 500; i++) {
        const x = Math.random() * size.width;
        const y = Math.random() * size.height;
        ctx.beginPath();
        ctx.arc(x, y, Math.random() * 1.5, 0, Math.PI * 2);
        ctx.fill();
      }
      
      // Reorder images: heroes first, then regular
      const heroItems = itemsWithImages.filter(item => heroImages.includes(item.id));
      const regularItems = itemsWithImages.filter(item => !heroImages.includes(item.id));
      const orderedItems = [...heroItems, ...regularItems];
      
      // Generate positions
      const heroIndices = heroItems.map((_, i) => i);
      const positions = generateMosaicPositions(
        orderedItems.length, 
        heroIndices, 
        size.width, 
        size.height
      );
      
      // Load all images
      const loadedImages = await Promise.all(
        orderedItems.map(item => loadImage(item.image_url).catch(() => null))
      );
      
      // Draw images
      for (let i = 0; i < loadedImages.length; i++) {
        const img = loadedImages[i];
        if (!img) continue;
        
        const pos = positions[i];
        
        ctx.save();
        
        // Subtle shadow for depth
        ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
        ctx.shadowBlur = pos.isHero ? 30 : 15;
        ctx.shadowOffsetX = 3;
        ctx.shadowOffsetY = 3;
        
        // Draw image with cover fit (no border)
        const imgAspect = img.width / img.height;
        const boxAspect = pos.width / pos.height;
        
        let srcX = 0, srcY = 0, srcW = img.width, srcH = img.height;
        
        if (imgAspect > boxAspect) {
          srcW = img.height * boxAspect;
          srcX = (img.width - srcW) / 2;
        } else {
          srcH = img.width / boxAspect;
          srcY = (img.height - srcH) / 2;
        }
        
        // Clip to rounded corners for a modern look
        const radius = pos.isHero ? 12 : 8;
        ctx.beginPath();
        ctx.roundRect(pos.x, pos.y, pos.width, pos.height, radius);
        ctx.clip();
        
        ctx.drawImage(
          img,
          srcX, srcY, srcW, srcH,
          pos.x, pos.y, pos.width, pos.height
        );
        
        ctx.restore();
      }
      
      // Draw header with logo and title
      const headerY = 45;
      
      // ProsperMind Logo
      ctx.save();
      ctx.translate(size.width / 2, headerY);
      
      // Logo circle
      ctx.beginPath();
      ctx.arc(0, 0, 28, 0, Math.PI * 2);
      ctx.fillStyle = '#D4AF37';
      ctx.fill();
      
      // Logo inner design
      ctx.strokeStyle = '#1a1a2e';
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.moveTo(-12, -8);
      ctx.lineTo(0, -16);
      ctx.lineTo(12, -8);
      ctx.lineTo(12, 8);
      ctx.lineTo(0, 16);
      ctx.lineTo(-12, 8);
      ctx.closePath();
      ctx.stroke();
      
      ctx.beginPath();
      ctx.moveTo(0, -16);
      ctx.lineTo(0, 16);
      ctx.moveTo(-12, -8);
      ctx.lineTo(12, 8);
      ctx.moveTo(12, -8);
      ctx.lineTo(-12, 8);
      ctx.stroke();
      
      ctx.restore();
      
      // Title
      ctx.fillStyle = '#D4AF37';
      ctx.font = 'bold 36px system-ui, -apple-system, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Prosper Mind', size.width / 2, headerY + 55);
      
      ctx.fillStyle = 'rgba(255, 255, 255, 0.6)';
      ctx.font = '18px system-ui, -apple-system, sans-serif';
      ctx.fillText('Vision Board', size.width / 2, headerY + 78);
      
      // Footer with date
      const footerY = size.height - 30;
      const today = new Date().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
      
      ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
      ctx.font = '16px system-ui, -apple-system, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(`Created on ${today}`, size.width / 2, footerY);
      
      // Generate preview URL
      const dataUrl = canvas.toDataURL('image/png', 1.0);
      setPreviewUrl(dataUrl);
      
    } catch (error) {
      console.error('Error generating collage:', error);
      toast.error('Failed to generate collage');
    } finally {
      setIsGenerating(false);
    }
  }, [itemsWithImages, selectedSize, heroImages, generateMosaicPositions]);

  // Proceed to step 2 and generate collage
  const handleProceedToPreview = () => {
    setStep(2);
    generateCollage();
  };

  // Regenerate when size changes in step 2
  useEffect(() => {
    if (step === 2 && itemsWithImages.length >= MIN_IMAGES_FOR_COLLAGE) {
      generateCollage();
    }
  }, [selectedSize, step, generateCollage, itemsWithImages.length]);

  // Download the collage
  const handleDownload = () => {
    if (!previewUrl) return;
    
    const link = document.createElement('a');
    link.download = `prosper-mind-vision-board-${selectedSize}-${Date.now()}.png`;
    link.href = previewUrl;
    link.click();
    
    toast.success('Vision board downloaded!');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden" data-testid="collage-generator-modal">
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-purple-50 to-indigo-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                <Layers className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900" data-testid="collage-modal-title">
                  {step === 1 ? 'Select Hero Images' : 'Vision Board Collage'}
                </h2>
                <p className="text-sm text-gray-500" data-testid="collage-modal-subtitle">
                  {step === 1 
                    ? `Choose up to 2 featured images (${heroImages.length}/2 selected)`
                    : `${itemsWithImages.length} images • Magazine-style layout`
                  }
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              data-testid="collage-modal-close-btn"
              className="p-2 hover:bg-white/50 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {step === 1 ? (
            /* Step 1: Hero Image Selection */
            <div>
              <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                <p className="text-sm text-amber-800">
                  <strong>Tip:</strong> Hero images will be displayed larger and more prominently. 
                  Select your most inspiring visions!
                </p>
              </div>
              
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {itemsWithImages.map((item, index) => {
                  const isHero = heroImages.includes(item.id);
                  const heroNumber = heroImages.indexOf(item.id) + 1;
                  
                  return (
                    <div
                      key={item.id}
                      onClick={() => toggleHeroImage(item.id)}
                      className={`relative aspect-square rounded-xl overflow-hidden cursor-pointer transition-all ${
                        isHero 
                          ? 'ring-4 ring-purple-500 ring-offset-2 scale-[1.02]' 
                          : 'hover:ring-2 hover:ring-gray-300'
                      }`}
                    >
                      <img 
                        src={item.image_url} 
                        alt={item.title}
                        className="w-full h-full object-cover"
                      />
                      
                      {/* Overlay for non-hero */}
                      {!isHero && (
                        <div className="absolute inset-0 bg-black/20 hover:bg-black/10 transition-colors" />
                      )}
                      
                      {/* Hero badge */}
                      {isHero && (
                        <div className="absolute top-2 left-2 w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-lg">
                          {heroNumber}
                        </div>
                      )}
                      
                      {/* Title overlay */}
                      <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/70 to-transparent">
                        <p className="text-white text-xs font-medium truncate">{item.title}</p>
                      </div>
                      
                      {/* Selection indicator */}
                      <div className={`absolute top-2 right-2 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                        isHero 
                          ? 'bg-purple-600 border-purple-600' 
                          : 'bg-white/80 border-gray-300'
                      }`}>
                        {isHero && <Check className="w-4 h-4 text-white" />}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            /* Step 2: Preview & Download */
            <div>
              {/* Page Size Selector */}
              <div className="mb-6" data-testid="page-size-selector">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Select Page Size
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(PAGE_SIZES).map(([key, size]) => (
                    <button
                      key={key}
                      onClick={() => setSelectedSize(key)}
                      data-testid={`page-size-${key}`}
                      className={`p-4 rounded-xl border-2 transition-all text-left ${
                        selectedSize === key
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-medium text-gray-900">{size.name}</div>
                      <div className="text-xs text-gray-500">{size.aspect}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Preview */}
              <div className="mb-6" data-testid="collage-preview-section">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Preview
                </label>
                <div className="relative bg-gray-100 rounded-xl overflow-hidden flex items-center justify-center min-h-[400px]" data-testid="collage-preview-container">
                  {isGenerating ? (
                    <div className="flex flex-col items-center gap-3" data-testid="collage-generating-indicator">
                      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600"></div>
                      <p className="text-sm text-gray-500">Generating your vision board...</p>
                    </div>
                  ) : previewUrl ? (
                    <img 
                      src={previewUrl} 
                      alt="Collage Preview" 
                      className="max-w-full max-h-[500px] object-contain rounded-lg shadow-lg"
                      data-testid="collage-preview-image"
                    />
                  ) : (
                    <p className="text-gray-400" data-testid="collage-preview-placeholder">Preview will appear here</p>
                  )}
                </div>
              </div>

              {/* Hidden canvas for generation */}
              <canvas ref={canvasRef} style={{ display: 'none' }} />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-between items-center">
          {step === 1 ? (
            <>
              <p className="text-sm text-gray-500">
                {heroImages.length === 0 
                  ? 'No hero images selected (optional)'
                  : `${heroImages.length} hero image${heroImages.length > 1 ? 's' : ''} selected`
                }
              </p>
              <div className="flex gap-3">
                <button
                  onClick={onClose}
                  className="px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-200 rounded-xl transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleProceedToPreview}
                  className="px-5 py-2.5 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-xl transition-colors flex items-center gap-2"
                >
                  Continue
                  <ChevronDown className="w-4 h-4 rotate-[-90deg]" />
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="text-sm text-gray-500" data-testid="collage-resolution-text">
                High-resolution PNG • {PAGE_SIZES[selectedSize].width} × {PAGE_SIZES[selectedSize].height}px
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setStep(1)}
                  className="px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-200 rounded-xl transition-colors"
                >
                  Back
                </button>
                <button
                  onClick={handleDownload}
                  disabled={!previewUrl || isGenerating}
                  data-testid="collage-download-btn"
                  className="px-5 py-2.5 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Download PNG
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Main Vision Module Component
const VisionModule = () => {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [categoryFilter, setCategoryFilter] = useState("ALL");
  const [isCollageModalOpen, setIsCollageModalOpen] = useState(false);

  // Fetch items
  const fetchItems = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "ALL") params.append("status", statusFilter);
      if (categoryFilter !== "ALL") params.append("category", categoryFilter);
      
      const response = await axios.get(
        `${API}/api/vision?${params.toString()}`,
        { headers: getAuthHeaders() }
      );
      setItems(response.data.items || []);
    } catch (error) {
      console.error("Error fetching vision items:", error);
      toast.error("Failed to load vision items");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, [statusFilter, categoryFilter]);

  // Save item (create or update)
  const handleSave = async (itemData, imageFile, itemId) => {
    try {
      let savedItem;
      
      if (itemId) {
        // Update existing item
        const response = await axios.put(
          `${API}/api/vision/${itemId}`,
          itemData,
          { headers: getAuthHeaders() }
        );
        savedItem = response.data.item;
      } else {
        // Create new item
        const response = await axios.post(
          `${API}/api/vision`,
          itemData,
          { headers: getAuthHeaders() }
        );
        savedItem = response.data.item;
      }
      
      // Upload image if provided
      if (imageFile && savedItem.id) {
        const formData = new FormData();
        formData.append("file", imageFile);
        
        await axios.post(
          `${API}/api/vision/${savedItem.id}/image`,
          formData,
          { 
            headers: { 
              ...getAuthHeaders(),
              "Content-Type": "multipart/form-data"
            }
          }
        );
      }
      
      toast.success(itemId ? "Vision updated" : "Vision created");
      fetchItems();
    } catch (error) {
      console.error("Error saving vision item:", error);
      throw error;
    }
  };

  // Delete item
  const handleDelete = async (itemId) => {
    if (!window.confirm("Are you sure you want to delete this vision item?")) return;
    
    try {
      await axios.delete(
        `${API}/api/vision/${itemId}`,
        { headers: getAuthHeaders() }
      );
      toast.success("Vision item deleted");
      fetchItems();
    } catch (error) {
      console.error("Error deleting vision item:", error);
      toast.error("Failed to delete vision item");
    }
  };

  // Update status
  const handleStatusChange = async (itemId, newStatus) => {
    try {
      await axios.put(
        `${API}/api/vision/${itemId}`,
        { status: newStatus },
        { headers: getAuthHeaders() }
      );
      
      if (newStatus === "ACHIEVED") {
        toast.success("Locked in. Keep building.");
      } else {
        toast.success("Status updated");
      }
      
      fetchItems();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update status");
    }
  };

  // Get unique categories from items
  const uniqueCategories = [...new Set(items.map(item => item.category))];

  // Check if user has items without images
  const hasItemsWithoutImages = items.some(item => !item.image_url);

  return (
    <div className="space-y-8" data-testid="vision-module">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" data-testid="vision-title">Vision</h1>
          <p className="text-gray-500 mt-1">Dream it. Define it. Visualize it.</p>
        </div>
        
        <button
          onClick={() => { setEditingItem(null); setIsModalOpen(true); }}
          data-testid="add-vision-item-btn"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-900 text-white rounded-xl font-medium hover:bg-gray-800 transition-colors shadow-lg shadow-gray-900/20"
        >
          <Plus className="w-5 h-5" />
          Add Vision Item
        </button>
      </div>

      {/* Nudge Banner - Items without images */}
      {items.length > 0 && hasItemsWithoutImages && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ImageIcon className="w-5 h-5 text-amber-600" />
            <p className="text-sm text-amber-800">Images make your vision real.</p>
          </div>
          <button 
            onClick={() => {
              const itemWithoutImage = items.find(item => !item.image_url);
              if (itemWithoutImage) {
                setEditingItem(itemWithoutImage);
                setIsModalOpen(true);
              }
            }}
            className="text-sm font-medium text-amber-700 hover:text-amber-800"
          >
            Add an image
          </button>
        </div>
      )}

      {/* Collage Progress Section */}
      {items.length > 0 && (
        <CollageProgressSection 
          items={items} 
          onOpenCollage={() => setIsCollageModalOpen(true)} 
        />
      )}

      {/* Filters */}
      {items.length > 0 && (
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Filter className="w-4 h-4" />
            <span>Filter:</span>
          </div>
          
          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:border-gray-400 focus:ring-0"
          >
            <option value="ALL">All Status</option>
            <option value="NOT_STARTED">Not Started</option>
            <option value="IN_PROGRESS">In Progress</option>
            <option value="ACHIEVED">Achieved</option>
          </select>
          
          {/* Category Filter */}
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:border-gray-400 focus:ring-0"
          >
            <option value="ALL">All Categories</option>
            {uniqueCategories.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>
      )}

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20" data-testid="vision-loading">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900"></div>
        </div>
      ) : items.length === 0 ? (
        <EmptyState onAdd={() => setIsModalOpen(true)} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="vision-items-grid">
          {items.map(item => (
            <VisionCard
              key={item.id}
              item={item}
              onEdit={(item) => { setEditingItem(item); setIsModalOpen(true); }}
              onDelete={handleDelete}
              onStatusChange={handleStatusChange}
            />
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <VisionModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setEditingItem(null); }}
        onSave={handleSave}
        editingItem={editingItem}
      />

      {/* Collage Generator Modal */}
      <CollageGeneratorModal
        isOpen={isCollageModalOpen}
        onClose={() => setIsCollageModalOpen(false)}
        items={items}
      />
    </div>
  );
};

export default VisionModule;
